// components/RotatingText.jsx
import React, { useRef, useEffect, useState } from 'react';

const RotatingText = ({ text, color = "white", size = 90, rotationSpeed = 20, glitch = false }) => {
    const canvasRef = useRef(null);
    const [glitchOffset, setGlitchOffset] = useState({ x: 0, y: 0 });

    // Glitch effect animation
    useEffect(() => {
        if (!glitch) return;

        const glitchInterval = setInterval(() => {
            // Random chance to trigger glitch (30% chance every interval)
            if (Math.random() < 0.3) {
                setGlitchOffset({
                    x: (Math.random() - 0.5) * 4,
                    y: (Math.random() - 0.5) * 4
                });
                // Reset after a short time
                setTimeout(() => {
                    setGlitchOffset({ x: 0, y: 0 });
                }, 50);
            }
        }, 150);

        return () => clearInterval(glitchInterval);
    }, [glitch]);

    useEffect(() => {
        if (!canvasRef.current) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        // Set canvas dimensions
        const canvasSize = size;
        canvas.width = canvasSize;
        canvas.height = canvasSize;

        // Clear canvas
        ctx.clearRect(0, 0, canvasSize, canvasSize);

        // Set text properties
        const radius = canvasSize / 2 - 20; // Slightly inset from edge
        ctx.font = '12px "Space Grotesk", sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = color;

        // Calculate spacing between characters
        const circumference = 2 * Math.PI * radius;
        const fontWeight = 300; // Adjust for lighter text

        // Draw text along circle path
        const repeatedText = `${text} - ${text} - `.toUpperCase();
        const characters = repeatedText.split('');

        const anglePerChar = (2 * Math.PI) / characters.length;

        characters.forEach((char, i) => {
            const angle = i * anglePerChar;

            // Position text
            const x = radius * Math.cos(angle) + canvasSize / 2;
            const y = radius * Math.sin(angle) + canvasSize / 2;

            // Save context
            ctx.save();

            // Move to character position
            ctx.translate(x, y);

            // Rotate to position character along the circular path
            ctx.rotate(angle + Math.PI / 2);

            // Glitch effect: draw multiple layers with slight offsets
            if (glitch && Math.random() < 0.15) {
                // Red channel
                ctx.fillStyle = 'rgba(255, 0, 0, 0.5)';
                ctx.fillText(char, -1, 0);

                // Blue channel
                ctx.fillStyle = 'rgba(0, 255, 255, 0.5)';
                ctx.fillText(char, 1, 0);

                // Main text
                ctx.fillStyle = color;
            }

            // Draw character
            ctx.fillText(char, 0, 0);

            // Restore context
            ctx.restore();
        });

    }, [text, color, size, glitch, glitchOffset]);

    // Use CSS animation for rotation
    return (
        <canvas
            ref={canvasRef}
            width={size}
            height={size}
            style={{
                animation: `rotate-text ${rotationSpeed}s linear infinite`,
                position: 'absolute',
                pointerEvents: 'none',
                transform: glitch ? `translate(${glitchOffset.x}px, ${glitchOffset.y}px)` : 'none',
                transition: glitch ? 'none' : 'transform 0.1s ease',
                filter: glitch ? 'contrast(1.2) brightness(1.1)' : 'none',
            }}
        />
    );
};

export default RotatingText;